import { BrowserRouter, Routes, Route, useLocation, useNavigate, NavLink, Navigate } from 'react-router-dom'
import Landing from './pages/Landing'
import Home from './pages/Home'
import Planning from './pages/Planning'
import Tournee from './pages/Tournee'
import Messages from './pages/Messages'
import Patients from './pages/PatientsListNew'
import Profile from './pages/Profil'
import PatientDetail from './pages/PatientDetail'
import AddAppointment from './pages/AddAppointment'
import DocumentsPage from './pages/DocumentsPage'
import Login from './pages/Login'
import Register from './pages/Register'
import PscCallback from './pages/PscCallback'
import Legal from './pages/Legal'

import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { websocketClient } from './services/websocket'
import { ProtectedRoute, PublicOnlyRoute } from './components/ProtectedRoute'
import { useUIStore, useAuthStore } from './stores'
import logo from './assets/medisync_logo.png'
import {
  IoHomeOutline, IoHome,
  IoCalendarOutline, IoCalendar,
  IoMapOutline, IoMap,
  IoFolderOutline, IoFolder,
  IoChatbubbleEllipsesOutline, IoChatbubbleEllipses,
  IoPersonOutline, IoPerson,
  IoSunnyOutline, IoMoonOutline,
  IoLogOutOutline,
  IoMenuOutline,
  IoCloseOutline,
  IoNotificationsOutline,
  IoChevronForwardOutline,
} from 'react-icons/io5'

const navItems = [
  { path: '/app', label: 'Accueil', icon: IoHomeOutline, iconActive: IoHome },
  { path: '/planning', label: 'Planning', icon: IoCalendarOutline, iconActive: IoCalendar },
  { path: '/tournee', label: 'Tournée', icon: IoMapOutline, iconActive: IoMap },
  { path: '/patients', label: 'Patients', icon: IoFolderOutline, iconActive: IoFolder },
  { path: '/messages', label: 'Messagerie', icon: IoChatbubbleEllipsesOutline, iconActive: IoChatbubbleEllipses },
  { path: '/profile', label: 'Mon profil', icon: IoPersonOutline, iconActive: IoPerson },
]

function Sidebar({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const { theme, toggleTheme } = useUIStore()
  const { user, logout, refreshToken } = useAuthStore()
  const location = useLocation()
  const navigate = useNavigate()

  const handleLogout = async () => {
    try {
      if (refreshToken) {
        const { authService } = await import('./services/api/auth')
        await authService.logout(refreshToken).catch(() => {})
      }
    } finally {
      logout()
      navigate('/', { replace: true })
    }
  }

  return (
    <aside
      className={`fixed left-0 top-0 h-full z-40 flex flex-col bg-white dark:bg-dark-100 border-r border-gray-200 dark:border-gray-800 transition-all duration-300 ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      {/* Logo */}
      <div className={`flex items-center h-24 border-b border-gray-200 dark:border-gray-800 px-3 ${collapsed ? 'justify-center' : 'justify-between'}`}>
        {!collapsed && (
          <div className='flex items-center'>
            <img src={logo} alt='MediSync' className='h-20 w-auto' />
          </div>
        )}
        {collapsed && (
          <img src={logo} alt='MediSync' className='h-12 w-12 object-contain' />
        )}
        {!collapsed && (
          <button
            onClick={onToggle}
            className='p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors'
          >
            <IoCloseOutline size={18} />
          </button>
        )}
      </div>

      {/* Nav links */}
      <nav className='flex-1 overflow-y-auto py-4 px-2 space-y-0.5'>
        {navItems.map(({ path, label, icon: Icon, iconActive: IconActive }) => {
          const active = location.pathname === path || (path !== '/app' && location.pathname.startsWith(path + '/'))
          return (
            <NavLink
              key={path}
              to={path}
              title={collapsed ? label : undefined}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium text-sm transition-all duration-150 group ${
                active
                  ? 'bg-primary-50 dark:bg-primary-600/15 text-primary-600 dark:text-primary-400'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              {active ? (
                <IconActive size={18} className='flex-shrink-0' />
              ) : (
                <Icon size={18} className='flex-shrink-0' />
              )}
              {!collapsed && <span className='truncate'>{label}</span>}
              {!collapsed && active && (
                <IoChevronForwardOutline size={14} className='ml-auto opacity-60' />
              )}
            </NavLink>
          )
        })}
      </nav>

      {/* Footer */}
      <div className='border-t border-gray-200 dark:border-gray-800 p-2 space-y-0.5'>
        <button
          onClick={toggleTheme}
          title={collapsed ? (theme === 'dark' ? 'Mode clair' : 'Mode sombre') : undefined}
          className='flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition-all'
        >
          {theme === 'dark' ? <IoSunnyOutline size={18} className='flex-shrink-0' /> : <IoMoonOutline size={18} className='flex-shrink-0' />}
          {!collapsed && <span>{theme === 'dark' ? 'Mode clair' : 'Mode sombre'}</span>}
        </button>
        <button
          onClick={() => void handleLogout()}
          title={collapsed ? 'Déconnexion' : undefined}
          className='flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all'
        >
          <IoLogOutOutline size={18} className='flex-shrink-0' />
          {!collapsed && <span>Déconnexion</span>}
        </button>

        {!collapsed && user && (
          <div className='flex items-center gap-2 px-3 py-2 mt-1'>
            <div className='w-7 h-7 rounded-full bg-primary-600 flex items-center justify-center flex-shrink-0'>
              <span className='text-white text-xs font-bold'>
                {user.firstName?.[0]}{user.lastName?.[0]}
              </span>
            </div>
            <div className='min-w-0'>
              <p className='text-xs font-semibold text-gray-900 dark:text-gray-100 truncate'>
                {user.firstName} {user.lastName}
              </p>
              <p className='text-[10px] text-gray-400 truncate'>{user.email}</p>
            </div>
          </div>
        )}
      </div>

      {/* Toggle quand collapsed */}
      {collapsed && (
        <button
          onClick={onToggle}
          className='absolute -right-3 top-20 w-6 h-6 bg-white dark:bg-dark-100 border border-gray-200 dark:border-gray-700 rounded-full flex items-center justify-center shadow-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors'
        >
          <IoMenuOutline size={12} className='text-gray-500' />
        </button>
      )}
    </aside>
  )
}

function Topbar({ sidebarCollapsed }: { sidebarCollapsed: boolean }) {
  const location = useLocation()
  const pageTitles: Record<string, string> = {
    '/': 'Tableau de bord',
    '/planning': 'Planning',
    '/tournee': 'Tournée du jour',
    '/patients': 'Patients',
    '/messages': 'Messagerie',
    '/documents/download': 'Documents',
    '/profile': 'Mon profil',
    '/add-appointment': 'Nouveau rendez-vous',
  }
  const title = Object.entries(pageTitles).find(([key]) =>
    key === location.pathname || (key !== '/' && location.pathname.startsWith(key + '/'))
  )?.[1] || 'MediSync'

  return (
    <header
      className={`fixed top-0 right-0 z-30 h-16 bg-white dark:bg-dark-100 border-b border-gray-200 dark:border-gray-800 flex items-center px-6 transition-all duration-300 ${
        sidebarCollapsed ? 'left-16' : 'left-60'
      }`}
    >
      <div className='flex items-center justify-between w-full'>
        <h1 className='text-lg font-semibold text-gray-900 dark:text-white'>{title}</h1>
        <div className='flex items-center gap-2'>
          <button className='relative p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors'>
            <IoNotificationsOutline size={20} />
          </button>
        </div>
      </div>
    </header>
  )
}

function AppShell({ children }: { children: React.ReactNode }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  return (
    <div className='flex h-screen bg-gray-50 dark:bg-dark-50 overflow-hidden'>
      <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} />
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-60'}`}>
        <Topbar sidebarCollapsed={sidebarCollapsed} />
        <main className='flex-1 overflow-hidden pt-16 flex flex-col'>
          {children}
        </main>
      </div>
    </div>
  )
}

function AppLayout() {
  return (
    <Routes>
      {/* Pages publiques */}
      <Route path='/' element={<Landing />} />
      <Route path='/login' element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
      <Route path='/register' element={<PublicOnlyRoute><Register /></PublicOnlyRoute>} />
      <Route path='/auth/psc/callback' element={<PscCallback />} />
      <Route path='/legal' element={<Legal />} />

      {/* Pages protégées — avec sidebar + topbar */}
      <Route path='/app' element={<ProtectedRoute><AppShell><div className='flex-1 overflow-y-auto'><Home /></div></AppShell></ProtectedRoute>} />
      <Route path='/planning' element={<ProtectedRoute><AppShell><div className='flex-1 overflow-y-auto'><Planning /></div></AppShell></ProtectedRoute>} />
      <Route path='/tournee' element={<ProtectedRoute><AppShell><div className='flex-1 overflow-y-auto'><Tournee /></div></AppShell></ProtectedRoute>} />
      <Route path='/add-appointment' element={<ProtectedRoute><AppShell><div className='flex-1 overflow-y-auto'><AddAppointment /></div></AppShell></ProtectedRoute>} />
      <Route path='/appointments/:id/edit' element={<ProtectedRoute><AppShell><div className='flex-1 overflow-y-auto'><AddAppointment /></div></AppShell></ProtectedRoute>} />
      <Route path='/messages' element={<ProtectedRoute><AppShell><Messages /></AppShell></ProtectedRoute>} />
      <Route path='/patients' element={<ProtectedRoute><AppShell><div className='flex-1 overflow-y-auto'><Patients /></div></AppShell></ProtectedRoute>} />
      <Route path='/patients/new' element={<ProtectedRoute><AppShell><div className='flex-1 overflow-y-auto'><PatientDetail /></div></AppShell></ProtectedRoute>} />
      <Route path='/patients/:id' element={<ProtectedRoute><AppShell><div className='flex-1 overflow-y-auto'><PatientDetail /></div></AppShell></ProtectedRoute>} />
      <Route path='/patients/:id/edit' element={<ProtectedRoute><AppShell><div className='flex-1 overflow-y-auto'><PatientDetail /></div></AppShell></ProtectedRoute>} />
      <Route path='/documents/download' element={<ProtectedRoute><AppShell><div className='flex-1 overflow-y-auto'><DocumentsPage /></div></AppShell></ProtectedRoute>} />
      <Route path='/profile' element={<ProtectedRoute><AppShell><div className='flex-1 overflow-y-auto'><Profile /></div></AppShell></ProtectedRoute>} />

      {/* Fallback */}
      <Route path='*' element={<Navigate to='/' replace />} />
    </Routes>
  )
}

export default function App() {
  const { theme } = useUIStore()
  const { user, setTokens, logout } = useAuthStore()

  useEffect(() => {
    const handleLogout = () => {
      websocketClient.disconnect()
      logout()
    }
    const handleTokenRefreshed = (e: Event) => {
      const { accessToken, refreshToken } = (e as CustomEvent).detail as { accessToken: string; refreshToken: string }
      setTokens(accessToken, refreshToken)
    }
    window.addEventListener('auth:logout', handleLogout)
    window.addEventListener('auth:tokenRefreshed', handleTokenRefreshed)
    return () => {
      window.removeEventListener('auth:logout', handleLogout)
      window.removeEventListener('auth:tokenRefreshed', handleTokenRefreshed)
    }
  }, [setTokens, logout])

  useEffect(() => {
    if (user?.id) {
      websocketClient.connect(String(user.id))
    }
  }, [user?.id])

  useEffect(() => {
    const handleReminder = (data: unknown) => {
      const reminder = data as { patientName: string; startDate: string; minutesBefore: number }
      const time = new Date(reminder.startDate).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
      toast(`Rappel : RDV avec ${reminder.patientName} dans ${reminder.minutesBefore} min (${time})`, {
        duration: 10000,
        icon: '🔔',
      })
    }
    websocketClient.on('appointment:reminder', handleReminder)
    return () => websocketClient.off('appointment:reminder', handleReminder)
  }, [])

  useEffect(() => {
    const root = document.documentElement
    if (theme === 'dark') root.classList.add('dark')
    else root.classList.remove('dark')
  }, [theme])

  return (
    <BrowserRouter>
      <AppLayout />
    </BrowserRouter>
  )
}
